import {
  createContext,
  useCallback,
  useContext,
  useState,
  FC,
  ReactNode,
} from 'react'

import { useSettingQuery, useUpdateUIConfigMutation } from '@/features/auth'

// 文字サイズモード (D10 → ADR-0019 で 3 値化).
// mode = 'large' / 'xlarge' で typography を拡大する (theme factory に scale を渡す).
//
// 永続化: server (UserUIConfig.font_size) を source of truth とし、 端末を跨いで
// 追従させる. localStorage は (1) 初回描画の flash 回避 (server 到着前の即時表示)
// (2) 未ログイン / back 未デプロイ時の fallback として残す. SSR 安全 (window guard).

export type FontScaleMode = 'default' | 'large' | 'xlarge'

const STORAGE_KEY = 'kakeru.fontScale'
// mode → 倍率の対応表 (= 拡大係数の唯一のノブ).
// large 1.25: 1.15 では年配薬剤師に不足との声を受け引き上げ (14px→17.5px 相当).
// xlarge 1.5: large でも不足の店舗向け (2026-07-23 アイセイ FB, ADR-0019).
const SCALES: Record<FontScaleMode, number> = {
  default: 1,
  large: 1.25,
  xlarge: 1.5,
}

const isFontScaleMode = (value: unknown): value is FontScaleMode =>
  value === 'default' || value === 'large' || value === 'xlarge'

interface FontScaleContextValue {
  mode: FontScaleMode
  setMode: (mode: FontScaleMode) => void
  scale: number
}

const FontScaleContext = createContext<FontScaleContextValue | undefined>(
  undefined
)

const readStoredMode = (): FontScaleMode => {
  if (typeof window === 'undefined') return 'default'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return isFontScaleMode(stored) ? stored : 'default'
}

export const FontScaleProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  // 初期値は localStorage (= 即時描画, flash 回避). server 到着で上書きする.
  const [mode, setModeState] = useState<FontScaleMode>(readStoredMode)

  const { data: setting } = useSettingQuery()
  const updateUIConfig = useUpdateUIConfigMutation()

  // server (ui_config.font_size) が source of truth. 取得できたら反映し、 localStorage も
  // 同期する (= 次回起動時の初期値を server 値に寄せる). 「server 値の変化時だけ state を
  // 調整する」 React 公式パターン (render 中の guarded setState. effect ではない) で、
  // 受信ごとの再 fetch ループを避ける. ui_config 不在 (back 未デプロイ / 未ログイン) 時は
  // 何もせず localStorage 値を維持する.
  const serverFontSize = setting?.ui_config?.font_size
  const [syncedServerFontSize, setSyncedServerFontSize] = useState<
    FontScaleMode | undefined
  >(undefined)
  if (
    isFontScaleMode(serverFontSize) &&
    serverFontSize !== syncedServerFontSize
  ) {
    setSyncedServerFontSize(serverFontSize)
    setModeState(serverFontSize)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, serverFontSize)
    }
  }

  const setMode = useCallback(
    (next: FontScaleMode) => {
      // 楽観更新 + localStorage cache. server 永続化は背景で実行し、
      // 失敗 (back 未デプロイ等) しても local の挙動は維持する.
      setModeState(next)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, next)
      }
      updateUIConfig.mutate({ key: 'font_size', value: next })
    },
    [updateUIConfig]
  )

  const scale = SCALES[mode]

  return (
    <FontScaleContext.Provider value={{ mode, setMode, scale }}>
      {children}
    </FontScaleContext.Provider>
  )
}

export const useFontScale = () => {
  const ctx = useContext(FontScaleContext)
  if (!ctx) {
    throw new Error('useFontScale must be used within FontScaleProvider')
  }
  return ctx
}
